import * as Yup from 'yup'
import { FormRegex, IRefDataDTO } from '../../../models/Common'
import {
  EmploymentTypes,
  YesNoTypes,
  EmployeerSpokenTypes,
  ReasonNotSpokenToEmployersTypes,
  PermissionToContactTypes,
} from '../../../models/Enquiry'
import { validateABN } from '../../../utilities/commonFunctions'

export interface IValidatorRefDataDTOItems {
  yesNoTypes: IRefDataDTO[]
  employmentTypes: IRefDataDTO[]
  reasonNotspokenTypes: IRefDataDTO[]
}

export class TellUsMoreValidator {
  private options: IValidatorRefDataDTOItems
  private unSureEmploymentTypeId: string
  private yesId: string
  private reasonNotspokenId: string
  private currentRules: any

  constructor(options: IValidatorRefDataDTOItems) {
    this.options = options
    this.unSureEmploymentTypeId = this.find(
      this.options.employmentTypes,
      EmploymentTypes.Unsure
    )
    this.yesId = this.find(this.options.yesNoTypes, YesNoTypes.Yes)
    this.reasonNotspokenId = this.find(
      this.options.reasonNotspokenTypes,
      ReasonNotSpokenToEmployersTypes.SeekingInformationOnly
    )
  }

  getRule(index: number) {
    this.currentRules = this.createRules(
      this.unSureEmploymentTypeId,
      this.yesId,
      this.reasonNotspokenId
    )
    return this.currentRules[index]
  }

  find(options: IRefDataDTO[], text: string) {
    if (options === null || options === undefined) {
      return ''
    }
    const selection = options.find(
      (item) => item.name.toLowerCase() === text.toLowerCase()
    )
    if (selection) {
      return selection.id
    }
    return ''
  }

  createRules(
    unSureEmploymentTypeId: string,
    yesId: string,
    reasonNotspokenId: string
  ) {
    return [
      Yup.object({
        businessABN: Yup.string()
          .required('This field cannot be empty, it is required.')
          .test(
            'businessABN',
            'Australian Business Number is invalid',
            (test) => validateABN(test)
          ),
        businessName: Yup.string().required(
          'This field cannot be empty, it is required.'
        ),
        tradingName: Yup.string()
          .required('This field cannot be empty, it is required.')
          .min(1, 'Requires a minimum of 1 characters')
          .max(200, 'Must be exactly 200 or less characters'),
        businessAddressLine1: Yup.string()
          .required('This field cannot be empty, it is required.')
          .min(1, 'Requires a minimum of 1 characters')
          .max(250, 'Must be exactly 250 or less characters'),
        businessSuburb: Yup.string().required(
          'This field cannot be empty, it is required.'
        ),
        businessPostcode: Yup.string().required(
          'This field cannot be empty, it is required.'
        ),
        businessState: Yup.string().required(
          'This field cannot be empty, it is required.'
        ),
        businessPhone: Yup.string()
          .required('This field cannot be empty, it is required.')
          .matches(FormRegex.Phone, 'Please enter a valid phone number')
          .min(6, 'Requires a minimum of 6 characters')
          .max(20, 'Must be exactly 20 or less characters'),
        noOfEmployees: Yup.string()
          .matches(FormRegex.PositiveNumber, 'Please enter a positive number')
          .max(4, 'Must be exactly 4 or less characters')
          .required('This field cannot be empty, it is required.'),
        employmentTypeId: Yup.string().required(
          'This field cannot be empty, it is required.'
        ),
        paidLeaveId: Yup.string()
          .when(['employmentTypeId', 'isenle', 'isEE'], {
            is: (employmentTypeId, isenle, isEE) => {
              return (
                employmentTypeId === unSureEmploymentTypeId && (isenle || isEE)
              )
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        apprenticeOrTraineeId: Yup.string().required(
          'This field cannot be empty, it is required.'
        ),
        personDescriptionIds: Yup.array()
          .required('This field cannot be empty, it is required.')
          .typeError('Atleast one option must be selected.')
          .min(1, 'Atleast one option must be selected'),
        visaHolder: Yup.boolean()
          .required('This field cannot be empty, it is required.')
          .oneOf([true, false], 'This field cannot be empty, it is required.')
          .nullable(true),
        visaTypeId: Yup.string()
          .when('visaHolder', {
            is: (visaHolder) => visaHolder === true,
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        countryId: Yup.string()
          .when(['visaHolder', 'isenle', 'isEE'], {
            is: (visaHolder, isenle, isEE) => {
              return visaHolder === true && (isenle === true || isEE === true)
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        languageId: Yup.string()
          .when('interpreterRequired', {
            is: (interpreterRequired) => interpreterRequired === true,
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        independentContractorId: Yup.string()
          .when(['isenle', 'isEE'], {
            is: (isenle, isEE) => {
              return isenle === true || isEE === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        genuineContractEmployeeTypeId: Yup.string()
          .when(['independentContractorId', 'isIC'], {
            is: (independentContractorId, isIC) => {
              return isIC === true || independentContractorId === yesId
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        dateOfBirth: Yup.string()
          .when(['isEE', 'isenle'], {
            is: (isEE, isenle) => {
              return isEE === true || isenle === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.')
              .matches(
                FormRegex.MinDate,
                'Please enter a valid date greater than minimum date (01/01/1900)'
              ),
          })
          .nullable(true),
        employeeSuburb: Yup.string()
          .when(['isEE', 'isenle'], {
            is: (isEE, isenle) => {
              return isEE === true || isenle === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        employeePostcode: Yup.string()
          .when(['isEE', 'isenle'], {
            is: (isEE, isenle) => {
              return isEE === true || isenle === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        employeeState: Yup.string()
          .when(['isEE', 'isenle'], {
            is: (isEE, isenle) => {
              return isEE === true || isenle === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
      }),
      Yup.object({
        occupation: Yup.string()
          .when(['isEE', 'isenle'], {
            is: (isEE, isenle) => {
              return isEE === true || isenle === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        mainDuties: Yup.string().required(
          'This field cannot be empty, it is required.'
        ),
        haveSpokenToEmployer: Yup.string()
          .when(['isEE', 'isenle'], {
            is: (isEE, isenle) => {
              return isEE === true || isenle === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        outcomeOfSpokenToEmployer: Yup.string()
          .when(['haveSpokenToEmployer'], {
            is: (haveSpokenToEmployer) => {
              return haveSpokenToEmployer === 'Yes'
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        notSpokenToEmployerReasonId: Yup.string()
          .when(['haveSpokenToEmployer'], {
            is: (haveSpokenToEmployer) => {
              return haveSpokenToEmployer === EmployeerSpokenTypes.NotYet
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        areOthersAffectedId: Yup.string()
          .when(['isEE', 'isenle', 'haveSpokenToEmployer'], {
            is: (isEE, isenle, haveSpokenToEmployer) => {
              return (
                haveSpokenToEmployer === EmployeerSpokenTypes.NotYet &&
                (isEE === true || isenle === true)
              )
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        employmentStartDate: Yup.string()
          .required('This field cannot be empty, it is required.')
          .matches(
            FormRegex.MinDate,
            'Please enter a valid date greater than minimum date (01/01/1900)'
          )
          .nullable(true),
        employmentEndDate: Yup.string()
          .when(['isenle'], {
            is: (isenle) => {
              return isenle === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.')
              .matches(
                FormRegex.MinDate,
                'Please enter a valid date greater than minimum date (01/01/1900)'
              ),
          })
          .nullable(true),
        hourlyPayRate: Yup.string()
          .when(['isEE', 'isenle'], {
            is: (isEE, isenle) => {
              return isEE === true || isenle === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        hasSupportingEvidence: Yup.boolean()
          .when(['isEE', 'isenle', 'notSpokenToEmployerReasonId'], {
            is: (isEE, isenle, notSpokenToEmployerReasonId) => {
              return (
                (isEE === true || isenle === true) &&
                notSpokenToEmployerReasonId !== null &&
                notSpokenToEmployerReasonId !== undefined &&
                notSpokenToEmployerReasonId !== reasonNotspokenId
              )
            },
            then: Yup.boolean()
              .required('This field cannot be empty, it is required.')
              .oneOf(
                [true, false],
                'This field cannot be empty, it is required.'
              )
              .nullable(true),
          })
          .nullable(true),
        supportingEvidenceIds: Yup.array()
          .when(
            [
              'isEE',
              'isenle',
              'notSpokenToEmployerReasonId',
              'hasSupportingEvidence',
            ],
            {
              is: (
                isEE,
                isenle,
                notSpokenToEmployerReasonId,
                hasSupportingEvidence
              ) => {
                return (
                  (isEE === true || isenle === true) &&
                  hasSupportingEvidence === true &&
                  notSpokenToEmployerReasonId !== reasonNotspokenId
                )
              },
              then: Yup.array()
                .required('This field cannot be empty, it is required.')
                .typeError('This field cannot be empty, it is required.')
                .min(1, 'Atleast one option must be selected'),
            }
          )
          .nullable(true),
        nominateSomeoneToAct: Yup.boolean()
          .when(['isEE', 'isenle', 'notSpokenToEmployerReasonId'], {
            is: (isEE, isenle, notSpokenToEmployerReasonId) => {
              return (
                (isEE === true || isenle === true) &&
                notSpokenToEmployerReasonId !== null &&
                notSpokenToEmployerReasonId !== undefined &&
                notSpokenToEmployerReasonId !== reasonNotspokenId
              )
            },
            then: Yup.boolean()
              .required('This field cannot be empty, it is required.')
              .oneOf(
                [true, false],
                'This field cannot be empty, it is required.'
              )
              .nullable(true),
          })
          .nullable(true),
        nomineeFullName: Yup.string()
          .when(['nominateSomeoneToAct'], {
            is: (nominateSomeoneToAct) => {
              return nominateSomeoneToAct === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.')
              .min(1, 'Requires a minimum of 1 characters')
              .max(200, 'Must be exactly 200 or less characters'),
          })
          .nullable(true),
        nomineeEmailAddress: Yup.string()
          .when(['nominateSomeoneToAct'], {
            is: (nominateSomeoneToAct) => {
              return nominateSomeoneToAct === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.')
              .matches(FormRegex.Email, 'Please enter a valid email address')
              .min(1, 'Requires a minimum of 6 characters')
              .max(200, 'Must be exactly 200 or less characters'),
          })
          .nullable(true),
        nomineePhone: Yup.string()
          .when(['nominateSomeoneToAct'], {
            is: (nominateSomeoneToAct) => {
              return nominateSomeoneToAct === true
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.')
              .matches(FormRegex.Phone, 'Please enter a valid phone number')
              .min(6, 'Requires a minimum of 6 characters')
              .max(20, 'Must be exactly 20 or less characters'),
          })
          .nullable(true),
        permissionToContact: Yup.string()
          .when(['isEE', 'isenle', 'notSpokenToEmployerReasonId'], {
            is: (isEE, isenle, notSpokenToEmployerReasonId) => {
              return (
                (isEE === true || isenle === true) &&
                notSpokenToEmployerReasonId !== null &&
                notSpokenToEmployerReasonId !== undefined &&
                notSpokenToEmployerReasonId !== reasonNotspokenId
              )
            },
            then: Yup.string()
              .required('This field cannot be empty, it is required.')
              .typeError('This field cannot be empty, it is required.'),
          })
          .nullable(true),
        employeerPhone: Yup.string()
          .when(['permissionToContact'], {
            is: (permissionToContact) => {
              return (
                permissionToContact === PermissionToContactTypes.Yes.toString()
              )
            },
            then: Yup.string()
              .matches(FormRegex.Phone, 'Please enter a valid phone number')
              .max(20, 'Must be exactly 20 or less characters')
              .nullable(true),
          })
          .nullable(true),
        employeerEmailAddress: Yup.string()
          .when(['permissionToContact'], {
            is: (permissionToContact) => {
              return (
                permissionToContact === PermissionToContactTypes.Yes.toString()
              )
            },
            then: Yup.string()
              .matches(FormRegex.Email, 'Please enter a valid email address')
              .max(200, 'Must be exactly 200 or less characters')
              .nullable(true),
          })
          .nullable(true),
      }),
      Yup.object({
        agreement: Yup.boolean().oneOf(
          [true],
          "'This field cannot be empty, it is required"
        ),
      }),
    ]
  }
}
